// src/services/productApi.js
// Product API client for React frontend to connect with PHP backend

class ProductAPI {
  constructor() {
    // Base URL - adjust this to match your PHP backend
    this.baseURL = process.env.REACT_APP_API_URL || 'https://transcontinentalnetwork.com/transcontinental-api';
    this.productsEndpoint = `${this.baseURL}/products`;
    
    // Default headers
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };
  }

  // Get JWT token from localStorage
  getAuthToken() {
    return localStorage.getItem('access_token');
  }

  // Get headers with optional authorization
  getHeaders(includeAuth = true) {
    const headers = { ...this.defaultHeaders };
    
    if (includeAuth) {
      const token = this.getAuthToken();
      // Only add auth header if token exists
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }
    
    return headers;
  }

  // Generic API request method with optional authentication
  async makeRequest(url, options = {}, requireAuth = false) {
    try {
      // Determine whether to include auth token
      const includeAuth = requireAuth || options.includeAuth !== false;
      
      const response = await fetch(url, {
        ...options,
        headers: {
          ...this.getHeaders(includeAuth),
          ...options.headers
        }
      });

      // Handle different response types
      const contentType = response.headers.get('content-type');
      let data;
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      if (!response.ok) {
        // PHP backend returns: { success: false, message: "...", error: "..." }
        const errorMessage = data.message || data.error || `HTTP ${response.status}: ${response.statusText}`;
        throw new Error(errorMessage);
      }

      // PHP backend returns: { success: true, data: {...}, message: "..." }
      // Return the FULL response with success and data intact
      return data;
    } catch (error) {
      console.error('API Request failed:', error);
      throw error;
    }
  }

  // IMAGE HANDLING METHODS

  /**
   * Convert image filename to full URL
   * @param {string} filename - Image filename
   * @returns {string} Full image URL
   */
  getImageUrl(filename) {
    if (!filename) return null;
    
    // If it's already a full URL, return as is
    if (filename.startsWith('http')) {
      return filename;
    }
    
    // Remove any leading path separators and uploads directory
    let cleanFilename = filename.replace(/^[\/\\]+/, '');
    cleanFilename = cleanFilename.replace(/^uploads[\/\\]/, '');
    
    // Construct the full URL
    return `${this.baseURL}/uploads/${cleanFilename}`;
  }

  /**
   * Process images array and convert to full URLs
   * @param {string[]} images - Array of image filenames
   * @returns {string[]} Array of full image URLs
   */
  processImageUrls(images) {
    if (!Array.isArray(images)) return [];
    
    return images
      .filter(img => img && img.trim())
      .map(img => this.getImageUrl(img.trim()));
  }

  /**
   * Process a product/service object to include full image URLs
   * @param {Object} item - Product or service object
   * @returns {Object} Processed item with full image URLs
   */
  processItemImages(item) {
    if (!item) return item;

    return {
      ...item,
      images: this.processImageUrls(item.images || []),
      primary_image: this.getImageUrl(item.primary_image),
      // Keep original data for editing purposes
      _original_images: item.images,
      _original_primary_image: item.primary_image
    };
  }

  /**
   * Process an array of items to include full image URLs
   * @param {Object[]} items - Array of products/services
   * @returns {Object[]} Processed items with full image URLs
   */
  processItemsImages(items) {
    if (!Array.isArray(items)) return items;
    
    return items.map(item => this.processItemImages(item));
  }

  // CATEGORY METHODS

  // Get all categories - PUBLIC ACCESS
  async getCategories(params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.search) queryParams.append('search', params.search);
    if (params.include_counts) queryParams.append('include_counts', params.include_counts);
    if (params.active_only !== undefined) queryParams.append('active_only', params.active_only);

    const url = `${this.productsEndpoint}/categories${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    
    // Public endpoint - auth token included if available but not required
    return await this.makeRequest(url, { includeAuth: true }, false);
  }

  // Create new category - REQUIRES ADMIN AUTH
  async createCategory(categoryData) {
    return await this.makeRequest(`${this.productsEndpoint}/categories`, {
      method: 'POST',
      body: JSON.stringify(categoryData)
    }, true);
  }

  // Update category - REQUIRES ADMIN AUTH
  async updateCategory(categoryId, categoryData) {
    return await this.makeRequest(`${this.productsEndpoint}/categories/${categoryId}`, {
      method: 'PUT',
      body: JSON.stringify(categoryData)
    }, true);
  }

  // Delete category - REQUIRES ADMIN AUTH
  async deleteCategory(categoryId) {
    return await this.makeRequest(`${this.productsEndpoint}/categories/${categoryId}`, {
      method: 'DELETE'
    }, true);
  }

  // PRODUCT METHODS

  // Get all products with filtering - PUBLIC ACCESS
  async getProducts(params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.search) queryParams.append('search', params.search);
    if (params.category_id) queryParams.append('category_id', params.category_id);
    if (params.min_price) queryParams.append('min_price', params.min_price);
    if (params.max_price) queryParams.append('max_price', params.max_price);
    if (params.featured_only) queryParams.append('featured_only', params.featured_only);
    if (params.page) queryParams.append('page', params.page);
    if (params.per_page) queryParams.append('per_page', params.per_page);
    if (params.include_inactive) queryParams.append('include_inactive', params.include_inactive);

    const url = `${this.productsEndpoint}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    
    // Public endpoint - auth token included if available but not required
    const response = await this.makeRequest(url, { includeAuth: true }, false);
    
    const data = response.data || response;
    
    // Process image URLs
    if (data.products) {
      data.products = this.processItemsImages(data.products);
    }
    
    return response;
  }

  // Get single product - PUBLIC ACCESS (with optional admin view for inactive items)
  async getProduct(productId) {
    // Public endpoint - auth token included if available (admins can view inactive products)
    const response = await this.makeRequest(`${this.productsEndpoint}/${productId}`, { includeAuth: true }, false);
    const data = response.data || response;
    
    // Process image URLs
    if (data.product) {
      data.product = this.processItemImages(data.product);
    }
    
    return response;
  }

  // Create new product - REQUIRES ADMIN AUTH
  async createProduct(productData) {
    // Use original image filenames for API call
    const apiData = {
      ...productData,
      images: productData._original_images || productData.images,
      primary_image: productData._original_primary_image || productData.primary_image
    };
    
    // Remove the temporary properties
    delete apiData._original_images;
    delete apiData._original_primary_image;
    
    const response = await this.makeRequest(`${this.productsEndpoint}`, {
      method: 'POST',
      body: JSON.stringify(apiData)
    }, true);
    
    const data = response.data || response;
    
    // Process response images
    if (data.product) {
      data.product = this.processItemImages(data.product);
    }
    
    return response;
  }

  // Update product - REQUIRES ADMIN AUTH
  async updateProduct(productId, productData) {
    // Use original image filenames for API call
    const apiData = {
      ...productData,
      images: productData._original_images || productData.images,
      primary_image: productData._original_primary_image || productData.primary_image
    };
    
    // Remove the temporary properties
    delete apiData._original_images;
    delete apiData._original_primary_image;
    
    const response = await this.makeRequest(`${this.productsEndpoint}/${productId}`, {
      method: 'PUT',
      body: JSON.stringify(apiData)
    }, true);
    
    const data = response.data || response;
    
    // Process response images
    if (data.product) {
      data.product = this.processItemImages(data.product);
    }
    
    return response;
  }

  // Delete product - REQUIRES ADMIN AUTH
  async deleteProduct(productId) {
    return await this.makeRequest(`${this.productsEndpoint}/${productId}`, {
      method: 'DELETE'
    }, true);
  }

  // SERVICE METHODS

  // Get all services with filtering - PUBLIC ACCESS
  async getServices(params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.search) queryParams.append('search', params.search);
    if (params.category_id) queryParams.append('category_id', params.category_id);
    if (params.min_price) queryParams.append('min_price', params.min_price);
    if (params.max_price) queryParams.append('max_price', params.max_price);
    if (params.featured_only) queryParams.append('featured_only', params.featured_only);
    if (params.page) queryParams.append('page', params.page);
    if (params.per_page) queryParams.append('per_page', params.per_page);
    if (params.include_inactive) queryParams.append('include_inactive', params.include_inactive);

    const url = `${this.productsEndpoint}/services${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    
    // Public endpoint - auth token included if available but not required
    const response = await this.makeRequest(url, { includeAuth: true }, false);
    
    const data = response.data || response;
    
    // Process image URLs
    if (data.services) {
      data.services = this.processItemsImages(data.services);
    }
    
    return response;
  }

  // Get single service - PUBLIC ACCESS (with optional admin view for inactive items)
  async getService(serviceId) {
    // Public endpoint - auth token included if available (admins can view inactive services)
    const response = await this.makeRequest(`${this.productsEndpoint}/services/${serviceId}`, { includeAuth: true }, false);
    const data = response.data || response;
    
    // Process image URLs
    if (data.service) {
      data.service = this.processItemImages(data.service);
    }
    
    return response;
  }

  // Create new service - REQUIRES ADMIN AUTH
  async createService(serviceData) {
    // Use original image filenames for API call
    const apiData = {
      ...serviceData,
      images: serviceData._original_images || serviceData.images,
      primary_image: serviceData._original_primary_image || serviceData.primary_image
    };
    
    // Remove the temporary properties
    delete apiData._original_images;
    delete apiData._original_primary_image;
    
    const response = await this.makeRequest(`${this.productsEndpoint}/services`, {
      method: 'POST',
      body: JSON.stringify(apiData)
    }, true);
    
    const data = response.data || response;
    
    // Process response images
    if (data.service) {
      data.service = this.processItemImages(data.service);
    }
    
    return response;
  }

  // Update service - REQUIRES ADMIN AUTH
  async updateService(serviceId, serviceData) {
    // Use original image filenames for API call
    const apiData = {
      ...serviceData,
      images: serviceData._original_images || serviceData.images,
      primary_image: serviceData._original_primary_image || serviceData.primary_image
    };
    
    // Remove the temporary properties
    delete apiData._original_images;
    delete apiData._original_primary_image;
    
    const response = await this.makeRequest(`${this.productsEndpoint}/services/${serviceId}`, {
      method: 'PUT',
      body: JSON.stringify(apiData)
    }, true);
    
    const data = response.data || response;
    
    // Process response images
    if (data.service) {
      data.service = this.processItemImages(data.service);
    }
    
    return response;
  }

  // Delete service - REQUIRES ADMIN AUTH
  async deleteService(serviceId) {
    return await this.makeRequest(`${this.productsEndpoint}/services/${serviceId}`, {
      method: 'DELETE'
    }, true);
  }

  // COMBINED METHODS

  // Get all items (products and services combined) - PUBLIC ACCESS
  async getAllItems(params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.search) queryParams.append('search', params.search);
    if (params.category_id) queryParams.append('category_id', params.category_id);
    if (params.type) queryParams.append('type', params.type);
    if (params.featured_only) queryParams.append('featured_only', params.featured_only);
    if (params.page) queryParams.append('page', params.page);
    if (params.per_page) queryParams.append('per_page', params.per_page);
    if (params.include_inactive) queryParams.append('include_inactive', params.include_inactive);

    const url = `${this.productsEndpoint}/all${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    
    // Public endpoint - auth token included if available but not required
    const response = await this.makeRequest(url, { includeAuth: true }, false);
    
    const data = response.data || response;
    
    // Process image URLs
    if (data.items) {
      data.items = this.processItemsImages(data.items);
    }
    
    return response;
  }

  // FILE UPLOAD METHODS

  // Upload files (images) - REQUIRES ADMIN AUTH
  async uploadFiles(files) {
    const formData = new FormData();
    
    // Add files to FormData
    if (Array.isArray(files)) {
      if (files.length === 0) {
        throw new Error('No files provided');
      }
      files.forEach(file => {
        formData.append('files[]', file, file.name);
      });
    } else if (files) {
      formData.append('files[]', files, files.name);
    } else {
      throw new Error('No files provided');
    }

    const token = this.getAuthToken();
    if (!token) {
      throw new Error('Authentication token not found. Please log in.');
    }

    try {
      const response = await fetch(`${this.productsEndpoint}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      // Handle different response types
      const contentType = response.headers.get('content-type');
      let data;
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      if (!response.ok) {
        throw new Error(data.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      // Return the FULL response
      // PHP returns: { success: true, data: { files: [...] }, message: "..." }
      return data;
    } catch (error) {
      console.error('Upload API Error:', error);
      throw error;
    }
  }

  // HELPER METHODS

  // Validate product data before sending
  validateProductData(productData) {
    const errors = [];

    if (!productData.name || productData.name.trim().length === 0) {
      errors.push('Product name is required');
    }

    if (!productData.description || productData.description.trim().length === 0) {
      errors.push('Product description is required');
    }

    if (!productData.price || productData.price <= 0) {
      errors.push('Product price must be greater than 0');
    }

    if (productData.is_on_sale && productData.sale_price) {
      if (productData.sale_price <= 0) {
        errors.push('Sale price must be greater than 0');
      }
      if (productData.sale_price >= productData.price) {
        errors.push('Sale price must be less than original price');
      }
    }

    if (productData.stock_quantity && productData.stock_quantity < 0) {
      errors.push('Stock quantity cannot be negative');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Validate service data before sending
  validateServiceData(serviceData) {
    const errors = [];

    if (!serviceData.name || serviceData.name.trim().length === 0) {
      errors.push('Service name is required');
    }

    if (!serviceData.description || serviceData.description.trim().length === 0) {
      errors.push('Service description is required');
    }

    if (!serviceData.price || serviceData.price <= 0) {
      errors.push('Service price must be greater than 0');
    }

    if (serviceData.is_on_sale && serviceData.sale_price) {
      if (serviceData.sale_price <= 0) {
        errors.push('Sale price must be greater than 0');
      }
      if (serviceData.sale_price >= serviceData.price) {
        errors.push('Sale price must be less than original price');
      }
    }

    const validPricingModels = ['fixed', 'hourly', 'project-based'];
    if (serviceData.pricing_model && !validPricingModels.includes(serviceData.pricing_model)) {
      errors.push('Invalid pricing model. Must be: fixed, hourly, or project-based');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Validate category data
  validateCategoryData(categoryData) {
    const errors = [];

    if (!categoryData.name || categoryData.name.trim().length === 0) {
      errors.push('Category name is required');
    }

    if (categoryData.name && categoryData.name.length > 100) {
      errors.push('Category name must be less than 100 characters');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Format price for display (US Dollar)
formatPrice(price, currency = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(price);
}

  // Calculate discount percentage
  calculateDiscount(originalPrice, salePrice) {
    if (!originalPrice || !salePrice || salePrice >= originalPrice) {
      return 0;
    }
    return Math.round(((originalPrice - salePrice) / originalPrice) * 100);
  }

  // Process features array (remove empty strings, trim whitespace)
  processFeatures(features) {
    if (!Array.isArray(features)) {
      return [];
    }
    return features
      .map(feature => feature.trim())
      .filter(feature => feature.length > 0);
  }

  // Process tags array (convert to lowercase, remove empty strings)
  processTags(tags) {
    if (!Array.isArray(tags)) {
      return [];
    }
    return tags
      .map(tag => tag.trim().toLowerCase())
      .filter(tag => tag.length > 0);
  }

  // Check if image URL is valid
  async checkImageUrl(url) {
    try {
      const response = await fetch(url, { method: 'HEAD' });
      return response.ok;
    } catch (error) {
      console.warn('Image URL check failed:', url, error);
      return false;
    }
  }

  // Get fallback image URL
  getFallbackImageUrl() {
    return '/images/placeholder.jpg';
  }

  // Handle API errors and return user-friendly messages
  handleAPIError(error) {
    const errorMessage = error.message || error.toString();

    if (errorMessage.includes('expired') || errorMessage.includes('Invalid or expired token')) {
      return {
        type: 'auth',
        message: 'Your session has expired. Please log in again.',
        shouldRedirect: true
      };
    }

    if (errorMessage.includes('No token provided') || errorMessage.includes('Unauthorized')) {
      return {
        type: 'auth',
        message: 'You need to be logged in to perform this action.',
        shouldRedirect: true
      };
    }

    if (errorMessage.includes('Admin access required') || errorMessage.includes('insufficient_permissions')) {
      return {
        type: 'permission',
        message: 'You do not have permission to perform this action.',
        shouldRedirect: false
      };
    }

    if (errorMessage.includes('validation') || errorMessage.includes('required')) {
      return {
        type: 'validation',
        message: errorMessage,
        shouldRedirect: false
      };
    }

    if (errorMessage.includes('not found') || errorMessage.includes('Not found')) {
      return {
        type: 'not_found',
        message: 'The requested item was not found.',
        shouldRedirect: false
      };
    }

    // Generic error
    return {
      type: 'generic',
      message: errorMessage || 'An unexpected error occurred. Please try again.',
      shouldRedirect: false
    };
  }
}

// Create and export a singleton instance
const productAPI = new ProductAPI();
export default productAPI;

// Named exports for specific functionality
export {
  ProductAPI
};